import { useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { resetPasswordSchema } from "./schema";
import { resetPasswordFormFields } from "./constants";

import logo from "@/assets/logo/promptlayer-cake-color.png";
import { useForgotPassword, useResetPassword } from "@/api/application-api";
import AuthSubmitButton from "@/components/AuthSubmitButton";
import { FormInput } from "@/components/ui/FormInput";
import { ToastType } from "@/enums";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { Button } from "@/components/ui/button";

const isExpiredError = (error: unknown): error is { error_code: "expired"; message: string } =>
  typeof error === "object" &&
  error !== null &&
  "error_code" in error &&
  (error as { error_code?: unknown }).error_code === "expired";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate: resetPassword, isLoading: isResetPasswordLoading } = useResetPassword();
  const { mutate: forgotPassword, isLoading: isForgotPasswordLoading } = useForgotPassword();
  const urlParams = new URLSearchParams(location.search);
  const emailFromURL = decodeURIComponent(urlParams.get("email") || "");
  const [linkExpired, setLinkExpired] = useState(false);

  const form = useForm<z.infer<typeof resetPasswordSchema>>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      email: emailFromURL,
      password: "",
      confirmPassword: "",
    },
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = async (values: z.infer<typeof resetPasswordSchema>) =>
    resetPassword(
      {
        code: urlParams.get("twilio_code") || "",
        email: values.email,
        password: values.password,
      },
      {
        onSuccess: data => {
          displayToast(data.message, ToastType.success);
          navigate("/login");
        },
        onError: (error: unknown) => {
          if (isExpiredError(error)) {
            setLinkExpired(true);
            displayErrorToast(error.message);
            return;
          }
          displayErrorToast(error);
        },
      },
    );

  const handleRequestNewLink = () => {
    const email = form.getValues("email") || emailFromURL;
    if (!email) {
      displayErrorToast("Please enter your email above first.");
      return;
    }
    forgotPassword(
      { email },
      {
        onSuccess: () => navigate("/login"),
      },
    );
  };

  return (
    <>
      <div className="flex flex-col items-center justify-center gap-2">
        <img src={logo} alt="logo" className="h-[52px] w-[52px]" />
        <span className="text-3xl font-bold text-gray-900">Reset Password</span>
      </div>
      {linkExpired && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-medium">This password reset link has expired.</p>
          <p className="mt-1">Reset links are valid for 10 minutes. Request a new one below.</p>
          <Button
            className="mt-3"
            onClick={handleRequestNewLink}
            isLoading={isForgotPasswordLoading}
            disabled={isForgotPasswordLoading}
          >
            Send me a new reset link
          </Button>
        </div>
      )}
      <form className="flex flex-col gap-3 py-3" onSubmit={form.handleSubmit(onSubmit)}>
        {resetPasswordFormFields.map(field => (
          <FormInput
            {...form.register(field.name as keyof z.infer<typeof resetPasswordSchema>)}
            key={field.name}
            label={field.label}
            placeholder={field.placeholder}
            type={field.type}
            error={
              form.formState.errors[field.name as keyof z.infer<typeof resetPasswordSchema>]
                ?.message
            }
          />
        ))}
        <div className="flex flex-col items-center gap-2">
          <AuthSubmitButton
            loading={isResetPasswordLoading}
            disabled={!form.formState.isValid || linkExpired}
            text="Reset Password"
          />
          <Button variant="inlineLink" onClick={() => navigate("/login")}>
            Back to Login
          </Button>
        </div>
      </form>
    </>
  );
};

export default ResetPassword;
